import React, { useMemo } from 'react';
import { Box, Button, Collapse, Paper as MuiPaper, Typography, useTheme, alpha, styled, IconButton, Tooltip } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Description as PageIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { vs2015 } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { copyToClipboard } from '../../../utils/clipboardUtils'; // Ensure this path is correct

const StyledContextPaper = styled(MuiPaper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  marginTop: theme.spacing(1),
  backgroundColor: theme.palette.mode === 'dark' 
    ? alpha(theme.palette.background.paper, 0.3) 
    : alpha(theme.palette.info.light, 0.08), // Light background for the context
  border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
  borderRadius: theme.shape.borderRadius,
}));

const PageContextDisplay = ({ messageId, content, timestamp, isExpanded, onToggle }) => {
  const theme = useTheme();

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).format(date);
  };

  const { displayContent, language, contentForCopy } = useMemo(() => {
    let processedContent = String(content || ''); // Ensure it's a string
    let lang = 'text';
    let copyText = processedContent;

    try {
      const trimmedContent = processedContent.trim();
      // Check if it looks like a JSON object or array
      if ((trimmedContent.startsWith('{') && trimmedContent.endsWith('}')) ||
          (trimmedContent.startsWith('[') && trimmedContent.endsWith(']'))) {
        const parsedJson = JSON.parse(trimmedContent);
        // Pretty print for display and copy
        const prettyJson = JSON.stringify(parsedJson, null, 2);
        processedContent = prettyJson;
        copyText = prettyJson;
        lang = 'json';
      } else {
        // Not JSON, treat as plain text. Remove "Page context:" prefix if present.
        const plainText = processedContent.replace(/^Page context:\s*/i, '').trim();
        processedContent = plainText;
        copyText = plainText; // Copy the cleaned plain text
      }
    } catch (e) {
      // Parsing failed, treat as plain text, remove prefix
      console.warn("PageContextDisplay: Failed to parse content as JSON, displaying as text.", content, e);
      const plainTextOnError = String(content || '').replace(/^Page context:\s*/i, '').trim();
      processedContent = plainTextOnError;
      copyText = plainTextOnError;
      lang = 'text';
    }
    return { displayContent: processedContent, language: lang, contentForCopy: copyText };
  }, [content]);


  const handleCopyContext = (e) => {
    e.stopPropagation(); // Prevent any parent click handlers
    copyToClipboard(contentForCopy, 'Page context copied!');
  };

  return (
    <Box sx={{ alignSelf: 'center', mb: 1, width: '100%', maxWidth: '90%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Button
            fullWidth
            variant="outlined"
            size="small"
            color="info"
            startIcon={<PageIcon fontSize="small" />} // Make icon size consistent
            endIcon={<ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}/>}
            onClick={() => onToggle(messageId)}
            sx={{ 
              borderRadius: 1, 
              textTransform: 'none', 
              justifyContent: 'space-between', // Pushes timestamp to the right
              flexGrow: 1,
              py: 0.75, // Slightly more padding for better touch/click target
            }}
            aria-expanded={isExpanded}
            aria-controls={`page-context-details-${messageId}`}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                    {isExpanded ? 'Hide Page Context' : 'Show Page Context'}
                </Typography>
                {timestamp && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', ml: 1 }}>
                      ({formatDate(timestamp)})
                    </Typography>
                )}
            </Box>
        </Button>
        {/* Conditionally render copy button only when expanded and content exists */}
        {isExpanded && displayContent && (
             <Tooltip title="Copy Page Context">
                <IconButton 
                  onClick={handleCopyContext} 
                  size="small" 
                  sx={{ml: 1}}
                  aria-label="Copy page context"
                >
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        )}
      </Box>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box id={`page-context-details-${messageId}`} role="region" aria-labelledby={`page-context-button-${messageId}`}> {/* For ARIA */}
          <StyledContextPaper elevation={0}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.75rem' }}>
              Page Context Details:
            </Typography>
            {displayContent ? (
              <SyntaxHighlighter
                style={vs2015}
                language={language}
                customStyle={{ 
                  fontSize: '0.8rem', 
                  maxHeight: '250px', // Consistent max height
                  overflowY: 'auto', 
                  backgroundColor: alpha(theme.palette.background.paper, 0.5), // Subtle background for code block
                  padding: theme.spacing(1), // Padding inside the highlighter
                  borderRadius: theme.shape.borderRadius / 2,
                }}
                wrapLines={true}
                PreTag="div" // Use div for PreTag
              >
                {displayContent}
              </SyntaxHighlighter>
            ) : (
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                (No page context available)
              </Typography>
            )}
          </StyledContextPaper>
        </Box>
      </Collapse>
    </Box>
  );
};

export default PageContextDisplay;